import {unwrap} from 'pages/kybDashboard/kybUtils';

/** Where Hub uploads live. The backend refuses any other bucket. */
export const HUB_BUCKET = 'daba-kyc-bucket';

export const HUB_PAGE_SIZE = 10;

export const DOCUMENT_STATUSES = ['PENDING', 'ACCEPTED', 'REJECTED'];

export const PROVENANCE_LABELS = {
  CUSTOMER: 'Customer',
  AGENT: 'Agent',
  PROVIDER: 'Provider',
};

/** Which dashboard permission governs each subject. */
export const SUBJECT_PERMISSION_TAB = {
  INDIVIDUAL: 'KYC_DASHBOARD',
  INSTITUTIONAL: 'KYB_DASHBOARD',
};

/**
 * One list request's `input`.
 *
 * Unset filters are dropped rather than sent as blanks, and `false` toggles
 * are dropped too — the API's own default already means "off". Subject and
 * filters ride along on every page, so paging never loses them.
 */
export const hubListInput = ({subjectType, subjectId}, page, filters = {}) => ({
  subjectType,
  subjectId,
  pagination: {page, limit: HUB_PAGE_SIZE},
  ...Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== '' &&
        value !== false,
    ),
  ),
});

/**
 * The S3 key for a new upload.
 *
 * The backend checks that the key sits under this subject's prefix, so the
 * prefix is built from the same `subjectType`/`subjectId` the register call
 * sends. The timestamp and random part keep a re-upload of `statement.pdf`
 * from overwriting the object an earlier version still points at.
 */
export const hubUploadKey = (
  subjectType,
  subjectId,
  fileName,
  unique = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
) => {
  const safeName =
    String(fileName || 'file')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^\w.-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'file';
  return `kyc-hub/${String(
    subjectType,
  ).toLowerCase()}/${subjectId}/${unique}-${safeName}`;
};

/** A presigned URL without its signature: the object's own address. */
export const objectUrlFromSignedUrl = signedUrl => {
  const url = new URL(signedUrl);
  return `${url.origin}${url.pathname}`;
};

/**
 * Puts one file into the KYC bucket under the subject's prefix.
 *
 * Returns `{fileUrl, fileName}` once the PUT has succeeded, or `{error}` with
 * a message to show. Only a failed presign request throws. The object is only
 * registered (by the caller) after this resolves, so a failed PUT never leaves
 * a document row pointing at nothing.
 */
export const uploadHubFile = async ({
  presign,
  file,
  subjectType,
  subjectId,
}) => {
  const contentType = file.type || 'application/octet-stream';
  const {data} = await presign({
    variables: {
      input: {
        bucket: HUB_BUCKET,
        filename: hubUploadKey(subjectType, subjectId, file.name),
        upload: true,
        contentType,
      },
    },
  });

  const {data: payload, error} = unwrap(data?.presignMediaURL);
  if (error) return {error};
  if (!payload?.url) return {error: 'Could not get an upload link.'};

  let response;
  try {
    response = await fetch(payload.url, {
      method: 'PUT',
      headers: {'Content-Type': contentType},
      body: file,
    });
  } catch (error) {
    // Almost always the bucket's CORS rules refusing this origin.
    return {error: 'The file could not reach storage. Nothing was saved.'};
  }
  if (!response.ok) return {error: 'The file did not upload. Try again.'};

  return {fileUrl: objectUrlFromSignedUrl(payload.url), fileName: file.name};
};

/** Whether a row is a live file rather than history. */
export const isActiveDocument = document =>
  !document?.deletedAt && !document?.replacedByDocumentId;

/** The document type that belongs to one declared beneficial owner. */
export const OWNER_ID_DOCUMENT_TYPE = 'BENEFICIAL_OWNER_ID';

/**
 * Whether this document type must name a beneficial owner. Only a business
 * has owners; the server refuses the field on an individual.
 */
export const needsBeneficialOwner = (subjectType, documentType) =>
  subjectType === 'INSTITUTIONAL' && documentType === OWNER_ID_DOCUMENT_TYPE;

/** A business's declared owners as select options, the administrator marked. */
export const beneficialOwnerOptions = (owners = []) =>
  (owners || [])
    .filter(owner => owner?.id)
    .map(owner => {
      const name =
        owner.fullName ||
        `${owner.firstName || ''} ${owner.lastName || ''}`.trim() ||
        'Unnamed owner';
      return {
        value: owner.id,
        label: owner.isPrimaryContact
          ? `${name} (account administrator)`
          : name,
      };
    });

/**
 * What an "Edit details" form would send, and what stops it.
 *
 * Only changed fields go out. Moving a file to `OTHER` always carries its
 * name, and moving it to a beneficial owner's ID always carries the owner,
 * because the server validates the new type against them even when they look
 * unchanged. `errors.changes` is set when there is nothing to send.
 */
export const documentDetailsChanges = (document, form, subjectType) => {
  const type = form?.documentType;
  const typeChanged = Boolean(type) && type !== document?.documentType;
  const freeName = (form?.freeName || '').trim();
  const fileName = (form?.fileName || '').trim();
  const ownerRequired = needsBeneficialOwner(subjectType, type);

  const changes = {
    ...(typeChanged && {documentType: type}),
    ...(type === 'OTHER' &&
      freeName &&
      (typeChanged || freeName !== (document?.freeName || '')) && {freeName}),
    ...(fileName && fileName !== (document?.fileName || '') && {fileName}),
    ...(ownerRequired &&
      form?.beneficialOwnerId &&
      (typeChanged ||
        form.beneficialOwnerId !== document?.beneficialOwnerId) && {
        beneficialOwnerId: form.beneficialOwnerId,
      }),
  };

  const errors = {
    documentType: !type && 'Pick a document type.',
    freeName: type === 'OTHER' && !freeName && 'Name this document.',
    fileName: !fileName && 'Give the file a name.',
    beneficialOwnerId:
      ownerRequired &&
      !form?.beneficialOwnerId &&
      'Pick the beneficial owner this ID belongs to.',
    reason: !(form?.reason || '').trim() && 'Give a reason.',
    changes: !Object.keys(changes).length && 'Nothing has been changed yet.',
  };

  return {changes, errors};
};

/** `previousText` / `previous_text` → `Previous text`. */
const humanizeKey = key => {
  const spaced = String(key)
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * An activity entry's `details`, as label/value pairs.
 *
 * `details` is action-specific — every event has its own keys, and events the
 * backend adds later will have keys nobody here has seen — so nothing is
 * assumed beyond "an object, or JSON text of one". Empty values are skipped.
 */
export const activityDetailEntries = details => {
  let value = details;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (error) {
      return value.trim() ? [['Details', value]] : [];
    }
  }
  if (!value || typeof value !== 'object') return [];

  return Object.entries(value)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([key, v]) => [
      humanizeKey(key),
      typeof v === 'object' ? JSON.stringify(v) : String(v),
    ]);
};

export const RISK_LEVELS = [
  {value: 'LOW', label: 'Low', color: 'green'},
  {value: 'MEDIUM', label: 'Medium', color: 'gold'},
  {value: 'HIGH', label: 'High', color: 'orange'},
  {value: 'VERY_HIGH', label: 'Very high', color: 'red'},
];

export const RISK_TAGS = [
  {value: 'PEP', label: 'Politically exposed person (PEP)'},
  {value: 'MEDIA_PERSONALITY', label: 'Media personality'},
];

const labelOf = (list, value) =>
  list.find(item => item.value === value)?.label || value;

export const riskLevelLabel = level => labelOf(RISK_LEVELS, level);
export const riskTagLabel = tag => labelOf(RISK_TAGS, tag);

/** A rating and its tags in one line, for the activity timeline. */
export const describeRisk = risk =>
  risk?.level || risk?.tags?.length
    ? [
        risk.level ? riskLevelLabel(risk.level) : 'No rating',
        ...(risk.tags || []).map(riskTagLabel),
      ].join(' · ')
    : 'Not assessed';

/**
 * A risk event's `details` with `from` and `to` as readable lines. A first
 * rating has no `from`, and a clear has no `to`: both read "Not assessed".
 */
export const riskActivityDetails = details => {
  let value = details;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (error) {
      return details;
    }
  }
  if (!value || typeof value !== 'object') return details;
  return {
    ...value,
    from: describeRisk(value.from),
    to: describeRisk(value.to),
  };
};
